import React, { useState, useEffect, useCallback } from 'react';
import {
    Container, Typography, Box, Paper, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, Button,
    TextField, Alert, Tooltip, Stack,
    Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
    useMediaQuery, useTheme, MenuItem
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useNavigate } from 'react-router-dom';
import LoadingOverlay from '../components/LoadingOverlay';
import FormDialog, { FormField, filledInputSx } from '../components/FormDialog';
import { useTranslation } from 'react-i18next';
import { sortCategoriesHierarchically } from '../utils/category';

const PREDEFINED_COLORS = [
    '#F44336', '#E91E63', '#9C27B0', '#673AB7', '#3F51B5',
    '#2196F3', '#03A9F4', '#00BCD4', '#009688', '#4CAF50',
    '#8BC34A', '#CDDC39', '#FFEB3B', '#FFC107', '#FF9800',
    '#FF5722', '#795548', '#9E9E9E', '#607D8B', '#000000'
];


const Categories = () => {
    const { t } = useTranslation();
    const { api } = useAuth();
    const { showToast } = useToast();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [categories, setCategories] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [validation, setValidation] = useState({ can_restore: true, conflicts: [] });
    const [restoring, setRestoring] = useState(false);

    // Dialog State
    const [openDialog, setOpenDialog] = useState(false);
    const [editingCategory, setEditingCategory] = useState(null);
    const [formData, setFormData] = useState({ name: '', description: '', color: '', parent_id: null, category_type: 'debit' });
    const [saving, setSaving] = useState(false);

    // Delete Dialog State
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [categoryToDelete, setCategoryToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);

    const fetchValidation = useCallback(async () => {
        try {
            const response = await api.get('/categories/restore/validate');
            setValidation(response.data);
        } catch (err) {
            console.error("Failed to fetch restore validation status", err);
        }
    }, [api]);

    const fetchCategories = useCallback(async () => {
        try {
            const response = await api.get('/categories/');
            setCategories(response.data);
            setError(null);
        } catch (err) {
            setError(t('Failed to fetch categories.'));
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [api, t]);

    useEffect(() => {
        fetchCategories();
        fetchValidation();
    }, [fetchCategories, fetchValidation]);


    const resetForm = useCallback(() => {
        setFormData({ name: '', description: '', color: '', parent_id: null, category_type: 'debit' });
        setEditingCategory(null);
    }, []);

    const handleOpenDialog = (category = null) => {
        if (category) {
            setEditingCategory(category);
            setFormData({
                name: category.name,
                description: category.description || '',
                color: category.color || '',
                parent_id: category.parent_id,
                category_type: category.category_type || 'debit'
            });
        } else {
            resetForm();
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        resetForm();
    };

    const handleSave = async () => {
        if (!formData.name.trim()) return;
        setSaving(true);
        try {
            if (editingCategory) {
                await api.put(`/categories/${editingCategory.id}`, formData);
            } else {
                await api.post('/categories/', formData);
            }
            await fetchCategories();
            handleCloseDialog();
        } catch (err) {
            console.error("Failed to save category", err);
            if (err.response?.data?.detail) {
                showToast(err.response.data.detail, "error");
            } else {
                showToast(t("Failed to save category. Please try again."), "error");
            }
        } finally {
            setSaving(false);
        }
    };

    const handleOpenDeleteDialog = (category) => {
        setCategoryToDelete(category);
        setDeleteDialogOpen(true);
    };

    const handleCloseDeleteDialog = () => {
        if (deleting) return;
        setDeleteDialogOpen(false);
        setCategoryToDelete(null);
    };

    const handleConfirmDelete = async () => {
        if (!categoryToDelete) return;
        setDeleting(true);
        try {
            await api.delete(`/categories/${categoryToDelete.id}`);
            showToast(t("Category deleted successfully."), "success");
            setDeleteDialogOpen(false);
            setCategoryToDelete(null);
            await fetchCategories();
        } catch (err) {
            console.error("Failed to delete category", err);
            const detail = err.response?.data?.detail;
            const errorMsg = typeof detail === 'string' ? detail : (detail?.message || t("Failed to delete category."));
            showToast(errorMsg, "error");
        } finally {
            setDeleting(false);
        }
    };

    const handleRestore = async () => {
        setRestoring(true);
        try {
            const res = await api.post('/categories/restore');
            showToast(res.data.detail || t("Successfully restored standard categories."), "success");
            await fetchCategories();
            await fetchValidation();
        } catch (err) {
            console.error("Failed to restore default categories", err);
            const errMsg = err.response?.data?.detail?.message || t("Failed to restore categories.");
            showToast(errMsg, "error");
        } finally {
            setRestoring(false);
        }
    };

    const buildHierarchy = (cats) => {
        return sortCategoriesHierarchically(cats, t);
    };
    const customCategories = buildHierarchy(categories.filter(row => row.user_id !== null));

    const tooltipTitle = !validation.can_restore
        ? `${t('Cannot restore standard categories:')}\n${validation.conflicts.join('\n')}`
        : t("Restore any deleted system categories");

    return (
        <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
            <LoadingOverlay open={loading} />
            <Box sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                mb: 4,
                gap: 2
            }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={() => navigate('/')} sx={{ mr: { xs: 1, sm: 2 } }} color="primary">
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4" sx={{
                        fontWeight: 700,
                        fontSize: { xs: '1.5rem', sm: '2.125rem' }
                    }}>
                        {t('Categories')}
                    </Typography>
                </Box>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                    <Tooltip title={tooltipTitle} style={{ whiteSpace: 'pre-line' }}>
                        <span>
                            <Button
                                variant="outlined"
                                disabled={!validation.can_restore || restoring}
                                onClick={handleRestore}
                                sx={{
                                    width: { xs: '100%', sm: 'auto' },
                                    py: { xs: 1.2, sm: 1 }
                                }}
                            >
                                {t('Restore Defaults')}
                            </Button>
                        </span>
                    </Tooltip>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                        sx={{
                            width: { xs: '100%', sm: 'auto' },
                            py: { xs: 1.2, sm: 1 }
                        }}
                    >
                        {t('Add Category')}
                    </Button>
                </Stack>
            </Box>


            {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

            {isMobile ? (
                <Stack spacing={2} data-testid="mobile-custom-categories">
                    {customCategories.length === 0 ? (
                        <Paper elevation={0} sx={{ p: 4, textAlign: 'center' }}>
                            <Typography color="text.secondary">{t('No custom categories found.')}</Typography>
                        </Paper>
                    ) : (
                        customCategories.map((row) => {
                            const subcategoryCount = categories.filter(c => c.parent_id === row.id).length;
                            const hasReferences = 
                                (row.transaction_count || 0) > 0 || 
                                (row.recurring_transaction_count || 0) > 0 || 
                                (row.scheduled_payment_count || 0) > 0 || 
                                (row.budget_item_count || 0) > 0 || 
                                subcategoryCount > 0;
                            const canDelete = !hasReferences;

                            const deleteTooltipTitle = canDelete ? (
                                t("Delete Category")
                            ) : (
                                <Box sx={{ p: 0.5 }}>
                                    <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                        {t('Cannot delete category:')}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                        • {t('Transactions')}: {row.transaction_count || 0}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                        • {t('Recurring Transactions')}: {row.recurring_transaction_count || 0}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                        • {t('Scheduled Transactions')}: {row.scheduled_payment_count || 0}
                                    </Typography>
                                    <Typography variant="caption" sx={{ display: 'block' }}>
                                        • {t('Budget Items')}: {row.budget_item_count || 0}
                                    </Typography>
                                    {subcategoryCount > 0 && (
                                        <Typography variant="caption" sx={{ display: 'block' }}>
                                            • {t('Subcategories')}: {subcategoryCount}
                                        </Typography>
                                    )}
                                </Box>
                            );

                            return (
                                <Paper key={row.id} elevation={0} sx={{ p: 2 }}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                            {row.color && (
                                                <Box sx={{ width: 14, height: 14, borderRadius: '50%', bgcolor: row.color, border: '1px solid rgba(0,0,0,0.1)' }} />
                                            )}
                                            <Box>
                                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                    {t(row.name) || row.name}
                                                </Typography>
                                                {row.description && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {row.description}
                                                    </Typography>
                                                )}
                                            </Box>
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                                            <Tooltip title={t("Edit Category")}>
                                                <IconButton size="small" onClick={() => handleOpenDialog(row)} color="primary" aria-label={`${t('Edit')} ${row.name}`} sx={{ mr: 0.5 }}>
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title={deleteTooltipTitle}>
                                                <span>
                                                    <IconButton 
                                                        size="small" 
                                                        onClick={() => handleOpenDeleteDialog(row)} 
                                                        color="error" 
                                                        disabled={!canDelete} 
                                                        aria-label={`${t('Delete')} ${row.name}`}
                                                    >
                                                        <DeleteIcon fontSize="small" />
                                                    </IconButton>
                                                </span>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </Paper>
                            );
                        })
                    )}
                </Stack>



            ) : (
                <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider' }} data-testid="desktop-custom-categories">
                    <Table>
                        <TableHead sx={{ bgcolor: 'background.default' }}>
                            <TableRow>
                                <TableCell sx={{ fontWeight: 600 }}>{t('Name')}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600 }}>{t('Actions')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {customCategories.map((row) => {
                                const subcategoryCount = categories.filter(c => c.parent_id === row.id).length;
                                const hasReferences = 
                                    (row.transaction_count || 0) > 0 || 
                                    (row.recurring_transaction_count || 0) > 0 || 
                                    (row.scheduled_payment_count || 0) > 0 || 
                                    (row.budget_item_count || 0) > 0 || 
                                    subcategoryCount > 0;
                                const canDelete = !hasReferences;

                                const deleteTooltipTitle = canDelete ? (
                                    t("Delete Category")
                                ) : (
                                    <Box sx={{ p: 0.5 }}>
                                        <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
                                            {t('Cannot delete category:')}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block' }}>
                                            • {t('Transactions')}: {row.transaction_count || 0}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block' }}>
                                            • {t('Recurring Transactions')}: {row.recurring_transaction_count || 0}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block' }}>
                                            • {t('Scheduled Transactions')}: {row.scheduled_payment_count || 0}
                                        </Typography>
                                        <Typography variant="caption" sx={{ display: 'block' }}>
                                            • {t('Budget Items')}: {row.budget_item_count || 0}
                                        </Typography>
                                        {subcategoryCount > 0 && (
                                            <Typography variant="caption" sx={{ display: 'block' }}>
                                                • {t('Subcategories')}: {subcategoryCount}
                                            </Typography>
                                        )}
                                    </Box>
                                );

                                return (
                                    <TableRow key={row.id} hover>
                                        <TableCell sx={{ pl: row.parent_id !== null ? 5 : 2 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                {row.parent_id !== null && (
                                                    <Typography sx={{ color: 'text.disabled', mr: 0.5 }}>↳</Typography>
                                                )}
                                                {row.color && (
                                                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: row.color }} />
                                                )}
                                                <Typography variant="body1" sx={{ fontWeight: row.parent_id === null ? 600 : 400 }}>
                                                    {t(row.name) || row.name}
                                                </Typography>
                                            </Box>
                                            {row.description && (
                                                <Typography variant="body2" color="text.secondary" sx={{ pl: row.parent_id !== null ? 2.5 : 0 }}>
                                                    {row.description}
                                                </Typography>
                                            )}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                                                {/* Edit Button - Only for custom categories */}
                                                <Tooltip title={t("Edit Category")}>
                                                    <IconButton size="small" onClick={() => handleOpenDialog(row)} sx={{ mr: 1, color: 'primary.main' }} aria-label={`${t('Edit')} ${row.name}`}>
                                                        <EditIcon fontSize="small" />
                                                    </IconButton>
                                                </Tooltip>

                                                {/* Delete Button - Disabled if there are references */}
                                                <Tooltip title={deleteTooltipTitle}>
                                                    <span>
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => handleOpenDeleteDialog(row)} 
                                                            sx={{ color: 'error.main' }} 
                                                            disabled={!canDelete} 
                                                            aria-label={`${t('Delete')} ${row.name}`}
                                                        >
                                                            <DeleteIcon fontSize="small" />
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                            {customCategories.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={2} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                        {t('No categories found.')}
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}


            {/* Category Form Dialog */}
            <FormDialog
                open={openDialog}
                onClose={handleCloseDialog}
                onSubmit={handleSave}
                title={editingCategory ? t('Edit Category') : t('New Category')}
                subtitle={editingCategory
                    ? t('Update the details of this category.')
                    : t('Define a custom category for your ledger entries.')
                }
                saveLabel={editingCategory ? t('Save Changes') : t('Create Category')}
                saving={saving}
                saveDisabled={!formData.name.trim()}
                testId="category-form-dialog"
            >
                {/* Type & Parent row */}
                {(() => {
                    // A category that already has children cannot be made a subcategory
                    const hasChildren = editingCategory
                        ? categories.some(c => c.parent_id === editingCategory.id)
                        : false;
                    return (
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mt: 1 }}>
                            <FormField label={t("Category Type")} required sx={{ flex: 1 }}>
                                <TextField
                                    autoFocus
                                    select
                                    value={formData.category_type}
                                    onChange={(e) => setFormData({ ...formData, category_type: e.target.value })}
                                    fullWidth
                                    size="small"
                                    variant="filled"
                                    InputProps={{ disableUnderline: true }}
                                    SelectProps={{ disableUnderline: true }}
                                    sx={filledInputSx}
                                >
                                    <MenuItem value="debit">{t("Expense (Debit)")}</MenuItem>
                                    <MenuItem value="credit">{t("Income (Credit)")}</MenuItem>
                                </TextField>
                            </FormField>
                            <FormField label={t("Parent Category")} sx={{ flex: 1 }}>
                                <TextField
                                    select
                                    value={formData.parent_id || ''}
                                    onChange={(e) => setFormData({ ...formData, parent_id: e.target.value ? parseInt(e.target.value) : null })}
                                    fullWidth
                                    size="small"
                                    variant="filled"
                                    InputProps={{ disableUnderline: true }}
                                    SelectProps={{ disableUnderline: true }}
                                    inputProps={{ 'aria-label': t('Parent Category (Optional)') }}
                                    disabled={hasChildren}
                                    sx={filledInputSx}
                                    helperText={hasChildren ? t('Cannot assign a parent — this category already has subcategories.') : undefined}
                                >
                                    <MenuItem value=""><em>{t("None (Top Level)")}</em></MenuItem>
                                    {/* Only show root categories; exclude self to prevent self-reference */}
                                    {categories
                                        .filter(c => c.id !== editingCategory?.id && c.parent_id === null)
                                        .sort((a, b) => (t(a.name || '')).localeCompare(t(b.name || ''), undefined, { sensitivity: 'base' }))
                                        .map(c => (
                                            <MenuItem key={c.id} value={c.id}>{t(c.name)} {c.user_id === null ? `(${t('System')})` : ''}</MenuItem>
                                        ))
                                    }
                                </TextField>
                            </FormField>
                        </Stack>
                    );
                })()}

                {/* Name */}
                <FormField label={t("Category Name")} required sx={{ mt: 2.5 }}>
                    <TextField
                        placeholder={t("e.g. Travel & Accommodation")}
                        fullWidth
                        size="small"
                        variant="filled"
                        InputProps={{ disableUnderline: true }}
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        sx={filledInputSx}
                        inputProps={{ 'aria-label': t('Category Name') }}
                    />
                </FormField>

                {/* Description */}
                <FormField label={t("Description")} sx={{ mt: 2.5 }}>
                    <TextField
                        placeholder={t("Optional — short description of this category")}
                        fullWidth
                        size="small"
                        variant="filled"
                        InputProps={{ disableUnderline: true }}
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        sx={filledInputSx}
                        inputProps={{ 'aria-label': t('Description (Optional)') }}
                    />
                </FormField>

                {/* Color picker */}
                <FormField label={t("Accent Color")} sx={{ mt: 2.5 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pt: 0.5 }}>
                        {/* No color option */}
                        <Box
                            onClick={() => setFormData({ ...formData, color: '' })}
                            title={t("No color")}
                            sx={{
                                width: 28, height: 28, borderRadius: '50%',
                                bgcolor: 'action.disabledBackground',
                                cursor: 'pointer',
                                border: !formData.color
                                    ? '2.5px solid'
                                    : '2.5px solid transparent',
                                borderColor: !formData.color ? 'primary.main' : 'transparent',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: '0.7rem', color: 'text.disabled',
                                transition: 'transform 0.15s ease',
                                '&:hover': { transform: 'scale(1.15)' },
                            }}
                        >
                            —
                        </Box>
                        {PREDEFINED_COLORS.map((colorHex) => (
                            <Box
                                key={colorHex}
                                onClick={() => setFormData({ ...formData, color: colorHex })}
                                aria-label={`${t('Select color')} ${colorHex}`}
                                title={colorHex}
                                sx={{
                                    width: 28, height: 28, borderRadius: '50%',
                                    bgcolor: colorHex,
                                    cursor: 'pointer',
                                    border: formData.color === colorHex
                                        ? '2.5px solid'
                                        : '2.5px solid transparent',
                                    borderColor: formData.color === colorHex ? 'primary.main' : 'transparent',
                                    outline: formData.color === colorHex
                                        ? `2px solid ${colorHex}40`
                                        : 'none',
                                    outlineOffset: '2px',
                                    transition: 'transform 0.15s ease',
                                    '&:hover': { transform: 'scale(1.15)' },
                                }}
                            />
                        ))}
                    </Box>
                    {formData.color && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: formData.color }} />
                            <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                                {formData.color}
                            </Typography>
                        </Box>
                    )}
                </FormField>
            </FormDialog>

            {/* Delete Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={handleCloseDeleteDialog}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
                PaperProps={{
                    sx: { borderRadius: 3, p: 1, minWidth: 320 }
                }}
            >
                <DialogTitle id="delete-dialog-title" sx={{ fontWeight: 600 }}>
                    {t("Delete Category?")}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        {t("Are you sure you want to delete category")} <strong>{categoryToDelete?.name}</strong>? {t("This action cannot be undone.")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleCloseDeleteDialog} color="inherit" disabled={deleting}>
                        {t("Cancel")}
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained" disabled={deleting} autoFocus>
                        {deleting ? t('Deleting...') : t('Delete')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default Categories;
